// storage-errors — Firebase Storage upload errors → i18n key.
//
// The SDK's messages are developer-facing ("Firebase Storage: User does
// not have permission…"); on a phone mid-upload the user needs to know
// whether to retry, shrink the file, or call the admin. Pure module,
// unit-tested; the caller resolves the key with `t()`.

export type StorageErrorKey =
  | 'storageUnauthorized'
  | 'storageQuota'
  | 'storageRetryLimit'
  | 'storageCanceled';

const BY_CODE: Record<string, StorageErrorKey> = {
  // Rules rejected the object: for us that is almost always "over 2 GB",
  // otherwise a rules/auth problem.
  'storage/unauthorized': 'storageUnauthorized',
  'storage/unauthenticated': 'storageUnauthorized',
  'storage/quota-exceeded': 'storageQuota',
  // The resumable upload retried for the whole retry window (network gone
  // for a long stretch) and gave up. A fresh start is the remedy.
  'storage/retry-limit-exceeded': 'storageRetryLimit',
  'storage/canceled': 'storageCanceled',
};

export function storageErrorCode(err: unknown): string {
  if (typeof err !== 'object' || err == null) return '';
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code : '';
}

export function isStorageCanceled(err: unknown): boolean {
  return storageErrorCode(err) === 'storage/canceled';
}

/** i18n key for a known code, else null (caller shows the raw code). */
export function storageErrorKey(err: unknown): StorageErrorKey | null {
  return BY_CODE[storageErrorCode(err)] ?? null;
}

/** Resolved message: translated for known codes, the raw `storage/…`
 * code for unknown ones, the Error message for non-Firebase errors. */
export function formatStorageError(err: unknown, t: (key: string) => string): string {
  const key = storageErrorKey(err);
  if (key) return t(key);
  const code = storageErrorCode(err);
  if (code) return code;
  return err instanceof Error ? err.message : String(err ?? '');
}
